import {
  AgentBetaLabel,
  CopilotChatCountBar,
  CopilotChats,
  CopilotName,
  CopilotSupportButton,
  useCopilotHistory,
  useNavigateToAgentChat,
  useCopilotSidebar,
} from '@/features/copilot';
import { close, useSidebarActive } from '@/features/sidebar';
import { useAnalytics } from '@/services/analytics';
import { useSafeNavigate } from '@/services/router/safe-navigate';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { Box, Button, Heading, Icon, PlainButton, Text, Tooltip, Truncated } from '@customerio/pluma-components/react';
import { useWorkspaceIdFromCurrentUrl } from '@/utils/workspace-id';
import { generatePath } from '@/services/router/generate-path';
import { useQueryState } from 'nuqs';
import { isInternalLink } from '@/utils/url';
import * as styles from './layout.css';
import { headerTitleAdjacentToSuffix } from '@/features/sidebar/header-title-adjacent.css';

import { useMountEffect } from '@/hooks/use-mount-effect';

interface AgentLayoutProps {
  children: React.ReactNode;
  title?: React.ReactNode;
}

export function AgentLayout({ children, title }: AgentLayoutProps) {
  const isCopilotSidebarActive = useSidebarActive('copilot');
  const navigateToChat = useNavigateToAgentChat();
  const { track } = useAnalytics();
  const { openCopilot } = useCopilotSidebar();
  const safeNavigate = useSafeNavigate();
  const [aiFrom] = useQueryState('ai-from');
  const isAiSkillsEnabled = useFeatureFlag(AccountFeatureFlag.AiSkills);

  const workspaceId = useWorkspaceIdFromCurrentUrl() ?? 0;
  const homePath = generatePath('/workspaces/:workspaceId/journeys', { workspaceId });
  const internalFrom = aiFrom != null ? isInternalLink(aiFrom) : null;
  const exitTarget = internalFrom?.relativePath ?? homePath;
  const history = useCopilotHistory();
  const totalChats = history.data?.pages?.[0]?.meta?.pagination?.total ?? 0;
  const hasLoadedChats = history.data != null;
  const isCollapsed = hasLoadedChats && totalChats === 0;

  useMountEffect(() => {
    if (isCopilotSidebarActive) {
      close();
    }
  });

  return (
    <div className={styles.agentLayout}>
      {/* History sidebar */}
      <div
        className={`${styles.historySidebar} ${isCollapsed ? styles.historySidebarCollapsed : ''}`}
        inert={isCollapsed || undefined}
      >
        <Box
          display="flex"
          alignItems="center"
          px="250"
          style={{
            borderBottom: `${themeVars.border['width-0125']} solid ${themeVars.color['border-minimal']}`,
            height: '45px',
            minHeight: '45px',
          }}
        >
          <Text size="s" weight="semibold" color="base">
            Recent chats
          </Text>
        </Box>
        {totalChats > 0 ? <CopilotChatCountBar total={totalChats} /> : null}
        <div className={styles.historySidebarContent}>
          <CopilotChats location="fullscreen" />
        </div>
        <div className={styles.historySidebarFooter}>
          <Box display="flex" flexDirection="column" px="150" py="150" gap="0">
            <PlainButton
              href={generatePath('/workspaces/:workspaceId/settings/ai-business-profile', { workspaceId })}
              className={styles.sidebarFooterLink}
            >
              <Icon name="sparkle" size="sm" color="subtle" />
              <Text size="s" weight="medium">
                Context
              </Text>
            </PlainButton>
            <PlainButton
              href={generatePath('/workspaces/:workspaceId/agent/routines', { workspaceId })}
              className={styles.sidebarFooterLink}
            >
              <Icon name="trigger-date" size="sm" color="subtle" />
              <Text size="s" weight="medium">
                Routines
              </Text>
            </PlainButton>
            {isAiSkillsEnabled ? (
              <PlainButton
                href={generatePath('/workspaces/:workspaceId/agent/skills', { workspaceId })}
                className={styles.sidebarFooterLink}
              >
                <Icon name="star" size="sm" color="subtle" />
                <Text size="s" weight="medium">
                  Skills
                </Text>
              </PlainButton>
            ) : null}
            <PlainButton href="/settings/privacy" className={styles.sidebarFooterLink}>
              <Icon name="manage" size="sm" color="subtle" />
              <Text size="s" weight="medium">
                AI settings
              </Text>
            </PlainButton>
          </Box>
        </div>
      </div>

      {/* Main panel */}
      <div className={styles.mainPanel}>
        {/* Header */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px="200"
          py="100"
          backgroundColor="base"
          className={styles.headerBar}
        >
          <Box display="flex" alignItems="center" gap="100" minW="0" flex={1}>
            {title === undefined ? (
              <>
                <Box className={headerTitleAdjacentToSuffix}>
                  <Truncated as={Heading} level="4">
                    <CopilotName />
                  </Truncated>
                </Box>
                <AgentBetaLabel />
              </>
            ) : (
              <Heading level="4">{title}</Heading>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap="100">
            <CopilotSupportButton />
            <Tooltip content="Exit full screen" isCustom={true}>
              {(context) => (
                <Button
                  variant="subtle"
                  size="sm"
                  icon="collapse"
                  isIconOnly={true}
                  onClick={() => {
                    track('aiAssistant', 'collapsedFromFullScreen', {});
                    openCopilot({ triggerSource: 'fullscreen_collapse' });
                    safeNavigate(exitTarget);
                  }}
                  ref={context.reference.setReference}
                  {...context.reference.getReferenceProps()}
                />
              )}
            </Tooltip>
            {!isCollapsed ? (
              <Button
                variant="primary"
                size="sm"
                icon="add"
                onClick={() => {
                  track('aiAssistant', 'newChatClicked', {});
                  navigateToChat();
                }}
              >
                New chat
              </Button>
            ) : null}
          </Box>
        </Box>

        {/* Content */}
        {children}
      </div>
    </div>
  );
}
